import { Loading, ChartGroup, getS3Object } from '@meteor/frontend-core';
import React, { useContext, useState, useEffect, useMemo } from 'react';
import { GLOBAL_EVENT } from '../../../../consts/common/global-event';
import { AppContext } from '../../../../contexts/AppContext';
import APIList from '../../../../http/ApiList';
import { componentDefinition } from '../../component-definition';
import { FIELD_CATEGORY, FIELD_IMPORTANT, PAGE_ID } from '../../../../enum/common.enum';
import dayjs from 'dayjs';

const ForecastChartGroup: React.FC<any> = (props) => {
  const { designMode, event$, menuCollapsed, scrollCount, setScrollCount, selectorTop, tralitSettings } = useContext(AppContext);
  const { property } = props;
  // Loading
  const [chartLoading, setChartLoading] = useState<any>(false);
  // Chart Data
  const [chartData, setChartData] = useState<any>([]);
  // 所有注册的Component
  const [allComponents, setAllComponents] = useState<any>(null);
  // KPI Fields
  const [kpiFields, setKpiFields] = useState([]);
  // 过滤条件
  const [filter, setFilter] = useState({});
  // 当前断面
  const [snapshot, setSnapshot] = useState();
  // URL Params
  const searchParams = new URLSearchParams(location.search);
  // 过滤条件
  const conditions = useMemo(() => {
    const params = (property.condition ? property.condition : Object.fromEntries(searchParams)) || {};
    if (Object.keys(params).length > 0) {
      return Object.keys(params).reduce((acc, cur) => {
        acc[cur] = params[cur].split(',');
        return acc;
      }, {});
    }
    return null;
  }, [property.condition]);

  /**
   * 获取Chart Data
   */
  const getChartData = (req) => {
    const {
      compare,
      dimension,
      currentSnapshot,
      targetSnapshot,
      currentPeriod = [],
      targetPeriod = [],
      aggregationPeriod,
      fiscalYear,
      drillFields,
    } = req;
    const [startDate, endDate] = aggregationPeriod || [];
    const paramMap = {
      snapshot: {
        snapshot: [currentSnapshot, targetSnapshot],
        compare: 'snapshot',
        reportType: dimension,
      },
      period: {
        snapshot: [currentSnapshot],
        compare: 'period',
        reportType: dimension,
        timeFilter: [
          {
            startDate: currentPeriod[0]?.startOf('month').format('YYYY-MM-DD'),
            endDate: currentPeriod[1]?.endOf('month').format('YYYY-MM-DD'),
          },
          {
            startDate: targetPeriod[0]?.startOf('month').format('YYYY-MM-DD'),
            endDate: targetPeriod[1]?.endOf('month').format('YYYY-MM-DD'),
          },
        ],
      },
      plan: {
        snapshot: [currentSnapshot],
        compare: 'plan',
        reportType: dimension,
      },
      prev_year: {
        snapshot: [currentSnapshot],
        compare: 'prev_year',
        reportType: dimension,
      },
      prev_month: {
        snapshot: [currentSnapshot],
        compare: 'prev_month',
        reportType: dimension,
      },
    };
    const params = {
      websocketRoute: 'WebsocketOpenaiEvent',
      client_account: tralitSettings.account,
      fiscalYear,
      compare,
      startDate: startDate?.format('YYYY-MM-DD'),
      endDate: endDate?.format('YYYY-MM-DD'),
      ...paramMap[compare],
      drillFields,
    };
    setChartLoading(true);
    APIList.getForecastChartData()
      .post(params)
      .then(async (res: any) => {
        const response = await getS3Object(process.env.REACT_APP_BUCKET_NAME, res.bucketKey);
        const { snapshot, fields, current, target, computed } = response;
        setSnapshot(snapshot);
        setKpiFields(fields.filter((field) => field.category === FIELD_CATEGORY.KPI_DATA && field.important === FIELD_IMPORTANT.LEVEL1));
        setChartData({ current, target, computed });
        setChartLoading(false);
      });
  };

  useEffect(() => {
    setChartLoading(true);
  }, [tralitSettings.account]);

  /**
   * 获取注册Component
   */
  useEffect(() => {
    if (componentDefinition) {
      setAllComponents({
        ...componentDefinition.StaticComponentDefinition,
        ...componentDefinition.CustomComponentDefinition,
      });
    }
  }, [componentDefinition]);

  /**
   * 事件监听：PAGE_INIT， FILTER_ITEM_CHANGED
   * 因为事件没有被消费，所以用globalEvent.data.aggregationPeriod判断事件执行条件
   */
  event$.useSubscription((globalEvent) => {
    if (!globalEvent || globalEvent.pageId !== PAGE_ID.FORECAST) {
      return;
    }
    if (globalEvent.eventType === GLOBAL_EVENT.PAGE_INIT) {
      setFilter(conditions || globalEvent.data);
      getChartData(conditions || globalEvent.data);
    }
    if (globalEvent.eventType === GLOBAL_EVENT.FILTER_ITEM_CHANGED) {
      setFilter(globalEvent.data);
      getChartData(globalEvent.data);
    }
  });

  return (
    <>
      {kpiFields && kpiFields.length > 0 ? (
        <ChartGroup
          height={'100%'}
          loading={chartLoading}
          isSmallMode
          syncScroll
          sizeMode={'small'}
          menuCollapsed={menuCollapsed}
          scrollCount={scrollCount}
          setScrollCount={setScrollCount}
          selectorTop={selectorTop}
          defaultItemCount={kpiFields.length < 4 ? kpiFields.length : 4}
        >
          {kpiFields.map((field) => {
            return (
              <div key={field.fieldKey} style={{ height: '100%' }}>
                {allComponents && allComponents['forecast-chart'] && allComponents['forecast-chart'].component ? (
                  React.cloneElement(allComponents['forecast-chart'].component, {
                    property: {
                      ...allComponents[field.chartType],
                      field: field,
                      filter,
                      snapshot,
                      dataSource: {
                        mockData: JSON.stringify(chartData),
                      },
                    },
                    disabled: !designMode,
                  })
                ) : field.copyFrom && allComponents && allComponents[field.copyFrom] && allComponents[field.copyFrom].component ? (
                  React.cloneElement(allComponents[field.copyFrom].component, {
                    property: allComponents[field.copyFrom], // 并不是原来的组件了
                    disabled: !designMode,
                  })
                ) : (
                  <></>
                )}
              </div>
            );
          })}
        </ChartGroup>
      ) : (
        <Loading />
      )}
    </>
  );
};

export default ForecastChartGroup;
